import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

/** One row of the admin compliance-type visibility catalog. */
export type ComplianceTypeVisibilityRow = {
  // filingCompositeId = ObligationRules.compositeId = ComplianceItem.type
  filingCompositeId: string;
  title: string | null;
  category: string | null;
  frequency: string | null;
  country: string | null;
  // Corporation type the rule applies to; null = applies to all.
  corporationType: string | null;
  hidden: boolean;
};

/** Available filter values, cascaded against the currently-applied filters. */
export type ComplianceTypeVisibilityFacets = {
  countries: string[];
  corporationTypes: string[];
  categories: string[];
};

export type ComplianceTypeVisibilityList = {
  types: ComplianceTypeVisibilityRow[];
  nextCursor: string | null;
  count: number;
  facets: ComplianceTypeVisibilityFacets;
};

export type ComplianceTypeVisibilityQuery = {
  cursor?: string;
  pageSize?: number;
  search?: string;
  country?: string;
  corporationType?: string;
  category?: string;
  visibility?: 'shown' | 'hidden';
};

/**
 * Reads a page of the obligation-rules catalog with each compliance type's current show/hide state,
 * server-filtered/searched/paginated. Admin-only (guarded server-side).
 */
export function useAdminComplianceTypeVisibility(params: ComplianceTypeVisibilityQuery, token?: string) {
  const query = new URLSearchParams();
  if (params.cursor) query.set('cursor', params.cursor);
  if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
  if (params.search) query.set('search', params.search);
  if (params.country) query.set('country', params.country);
  if (params.corporationType) query.set('corporationType', params.corporationType);
  if (params.category) query.set('category', params.category);
  if (params.visibility) query.set('visibility', params.visibility);

  const {
    data,
    error,
    isLoading,
    mutate,
  }: {
    data?: ComplianceTypeVisibilityList;
    error?: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<ComplianceTypeVisibilityList>;
  } = useSWR(`compliance-type-visibility?${query.toString()}`, SWRFetcher({ method: 'GET' }, token));

  return { data, error, isLoading, mutate };
}
